import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getAdminTenants,
  getAdminTenantMarketing,
  getAdminTenantDeletions,
  getAdminTenantDeletion,
  createTenantDeletion,
  AdminTenantsQuery,
  AdminTenantDeletionsQuery,
  CreateTenantDeletionPayload,
  CreateTenantDeletionResponse,
} from "../services/tenantDeletionApi";

const ADMIN_TENANTS_KEY = ["adminTenants"] as const;
export const ADMIN_TENANT_MARKETING_KEY = ["adminTenantMarketing"] as const;
const ADMIN_TENANT_DELETIONS_KEY = ["adminTenantDeletions"] as const;
const ADMIN_TENANT_DELETION_KEY = ["adminTenantDeletion"] as const;

/**
 * GET /admin/tenants — paginated platform-level tenant list (AC-28).
 */
export const useAdminTenants = (query: AdminTenantsQuery = {}) => {
  return useQuery({
    queryKey: [...ADMIN_TENANTS_KEY, query],
    queryFn: () => getAdminTenants(query),
    staleTime: 30 * 1000,
  });
};

/**
 * GET /admin/tenants/:id/marketing — consents + opt-out flag for the
 * "Marketing i sprzedaż" tab (AC-4).
 */
export const useAdminTenantMarketing = (tenantId: string | undefined) => {
  return useQuery({
    queryKey: [...ADMIN_TENANT_MARKETING_KEY, tenantId],
    queryFn: () => getAdminTenantMarketing(tenantId as string),
    enabled: !!tenantId,
    staleTime: 30 * 1000,
  });
};

/**
 * GET /admin/tenant-deletions — paginated archive list (AC-29).
 */
export const useAdminTenantDeletions = (
  query: AdminTenantDeletionsQuery = {}
) => {
  return useQuery({
    queryKey: [...ADMIN_TENANT_DELETIONS_KEY, query],
    queryFn: () => getAdminTenantDeletions(query),
    staleTime: 30 * 1000,
  });
};

/**
 * GET /admin/tenant-deletions/:id — archive detail (AC-30).
 */
export const useAdminTenantDeletion = (id: string | undefined) => {
  return useQuery({
    queryKey: [...ADMIN_TENANT_DELETION_KEY, id],
    queryFn: () => getAdminTenantDeletion(id as string),
    enabled: !!id,
    staleTime: 60 * 1000,
  });
};

/**
 * POST /admin/tenant-deletions — Phase 1 deletion trigger (AC-1).
 *
 * Per spec: caller is responsible for surfacing toast/error mapping.
 * This hook only handles cache invalidation on success.
 */
export const useDeleteTenant = () => {
  const queryClient = useQueryClient();

  return useMutation<
    CreateTenantDeletionResponse,
    Error,
    CreateTenantDeletionPayload
  >({
    mutationFn: (payload: CreateTenantDeletionPayload) =>
      createTenantDeletion(payload),
    onSuccess: () => {
      // Refresh both lists: tenant disappears from /admin/tenants, archive appears in /admin/tenant-deletions
      queryClient.invalidateQueries({ queryKey: ADMIN_TENANTS_KEY });
      queryClient.invalidateQueries({ queryKey: ADMIN_TENANT_DELETIONS_KEY });
    },
  });
};
